import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Pencil, CheckCircle, Archive, ExternalLink,
  Shield, Scale, FileText, Loader2, Clock,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { legalAPI } from '@/lib/api';
import { cn } from '@/lib/utils';

const SCAN_METHOD_LABELS = {
  keyword_search: 'Keyword Search',
  element_check: 'Element Check',
  api_check: 'API Check',
  port_scan: 'Port Scan',
  external_db_lookup: 'External DB Lookup',
  content_analysis: 'Content Analysis',
};

const statusStyles = {
  draft: 'border-vrs-amber/30 text-vrs-amber bg-vrs-amber/10',
  approved: 'border-vrs-green/30 text-vrs-green bg-vrs-green/10',
  archived: 'border-muted-foreground/30 text-muted-foreground bg-muted/20',
};

export default function LawDetail() {
  const { lawId } = useParams();
  const navigate = useNavigate();
  const [law, setLaw] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await legalAPI.getLaw(lawId);
        setLaw(res.data);
      } catch { toast.error('Failed to load law'); navigate('/laws'); }
      setLoading(false);
    };
    fetch();
  }, [lawId, navigate]);

  const handleApprove = async () => {
    try {
      await legalAPI.approveLaw(lawId);
      toast.success('Law approved!');
      const res = await legalAPI.getLaw(lawId);
      setLaw(res.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleArchive = async () => {
    try {
      await legalAPI.archiveLaw(lawId);
      toast.success('Status changed');
      const res = await legalAPI.getLaw(lawId);
      setLaw(res.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  if (!law) return null;

  const params = law.parameters || [];
  const fines = law.potential_fines || [];
  const pdfs = law.pdf_uploads || [];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="law-detail-page">
      {/* Header */}
      <div className="flex items-start gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/laws')}><ArrowLeft className="h-4 w-4" /></Button>
        <div className="flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl font-semibold tracking-tight">{law.title}</h1>
            <Badge variant="outline" className={cn('text-[10px] capitalize', statusStyles[law.status] || statusStyles.draft)}>
              {law.status || 'draft'}
            </Badge>
          </div>
          <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
            {(law.categories || [law.category]).filter(Boolean).map((c, i) => (
              <Badge key={i} variant="secondary" className="text-[10px]">{c}</Badge>
            ))}
            {law.jurisdiction && <span>{law.jurisdiction}</span>}
            {law.source_url && (
              <a href={law.source_url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline flex items-center gap-1">
                <ExternalLink className="h-3 w-3" />Source
              </a>
            )}
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          {law.status === 'draft' && (
            <Button variant="outline" size="sm" onClick={() => navigate(`/laws/${lawId}/edit`)} data-testid="detail-edit-button">
              <Pencil className="mr-1 h-4 w-4" />Edit
            </Button>
          )}
          {law.status === 'draft' && (
            <Button size="sm" onClick={handleApprove} data-testid="detail-approve-button">
              <CheckCircle className="mr-1 h-4 w-4" />Approve
            </Button>
          )}
          {law.status !== 'draft' && law.status !== 'archived' && (
            <div className="flex items-center gap-2 rounded-lg border border-border/40 bg-muted/10 px-3 py-1.5">
              <p className="text-xs text-muted-foreground">Archive this law and create a new version to edit</p>
            </div>
          )}
          <Button variant="outline" size="sm" onClick={handleArchive}>
            {law.status === 'archived' ? <><Clock className="mr-1 h-4 w-4" />Restore</> : <><Archive className="mr-1 h-4 w-4" />Archive</>}
          </Button>
        </div>
      </div>

      {/* Description */}
      {law.description && (
        <Card className="border-border/60">
          <CardContent className="p-5">
            <p className="text-sm text-foreground/90 leading-relaxed">{law.description}</p>
          </CardContent>
        </Card>
      )}

      {/* PDFs */}
      {pdfs.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          {pdfs.map(pdf => (
            <Badge key={pdf.file_id} variant="outline" className="flex items-center gap-1 text-xs">
              <FileText className="h-3 w-3" />{pdf.filename}
            </Badge>
          ))}
        </div>
      )}

      {/* Parameters */}
      <div>
        <h2 className="text-sm font-semibold tracking-wide uppercase text-muted-foreground mb-3">Scannable Parameters ({params.length})</h2>
        {params.length === 0 ? (
          <p className="text-sm text-muted-foreground">No parameters defined.</p>
        ) : (
          <div className="space-y-2">
            {params.map((p, i) => (
              <Card key={i} className="border-border/40">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <Shield className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                    <div className="flex-1 space-y-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-semibold">{p.parameter_name}</p>
                        <Badge variant="outline" className="text-[10px]">{SCAN_METHOD_LABELS[p.scan_method] || p.scan_method}</Badge>
                        <Badge variant={p.is_externally_scannable ? 'outline' : 'secondary'} className={cn('text-[10px]', p.is_externally_scannable ? 'border-vrs-green/30 text-vrs-green' : 'text-vrs-amber')}>
                          {p.is_externally_scannable ? 'Scannable' : 'Internal Audit'}
                        </Badge>
                        <span className="text-[10px] text-muted-foreground font-mono">Weight: {p.weight}</span>
                      </div>
                      {p.compliance_criteria && <p className="text-xs text-muted-foreground">Criteria: {p.compliance_criteria}</p>}
                      {p.scan_config && Object.keys(p.scan_config).some(k => {
                        const v = p.scan_config[k];
                        return v && (Array.isArray(v) ? v.length > 0 : v !== '');
                      }) && (
                        <div className="text-[10px] text-muted-foreground/70 font-mono mt-1 bg-muted/20 rounded px-2 py-1">
                          {JSON.stringify(Object.fromEntries(Object.entries(p.scan_config).filter(([, v]) => v && (Array.isArray(v) ? v.length > 0 : v !== ''))))}
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Fines */}
      {fines.length > 0 && (
        <div>
          <h2 className="text-sm font-semibold tracking-wide uppercase text-muted-foreground mb-3">Fine Schedule ({fines.length})</h2>
          <div className="space-y-2">
            {fines.map((f, i) => (
              <div key={i} className="flex items-center gap-3 rounded-lg border border-vrs-red/20 px-4 py-3">
                <Scale className="h-4 w-4 text-vrs-red shrink-0" />
                <div>
                  <p className="text-sm font-medium">{f.violation_description}</p>
                  <p className="text-xs text-muted-foreground">
                    ${(f.min_amount || 0).toLocaleString()} - ${(f.max_amount || 0).toLocaleString()} {f.currency || 'USD'} ({(f.frequency || 'per_incident').replace('_', ' ')})
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
